import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Snackbar, Alert as MuiAlert } from "@mui/material";
import axiosInstance from "../axiosinstance";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import "../Forms/Forms.css";
import { FiEye, FiEyeOff, FiX } from "react-icons/fi";

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  phone_number: Yup.string()
    .matches(/^\d{10}$/, "Phone number must be 10 digits")
    .required("Phone number is required"),
  role: Yup.object()
    .shape({
      value: Yup.string().required("Role is required"),
      label: Yup.string(),
    })
    .nullable()
    .required("Role is required"),
});

export default function EditUserForm({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const { user_id } = useParams();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [roles, setRoles] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone_number: "",
      role: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const response = await axiosInstance.put(
          `/api/edit_system_user/${user_id}`,
          { ...values, role: values.role.value }
        );
        if (response.status === 200) {
          setSnackbarSeverity("success");
          setSnackbarMessage("User updated successfully!");
          setSnackbarOpen(true);
          setTimeout(() => navigate("/system-users"), 2000);
        } else {
          
          setSnackbarSeverity("error");
          setSnackbarMessage("Error updating user. Please try again.");
          setSnackbarOpen(true);
        }
      } catch (error) {
        if (error.status === 400) {
          setSnackbarSeverity("error");
          setSnackbarMessage(error.response?.data?.message || "Error updating user. Please try again.");
          setSnackbarOpen(true);
          return;
        }
        console.error("Error updating user:", error);
        setSnackbarSeverity("error");
        setSnackbarMessage("Error updating user. Please try again.");
        setSnackbarOpen(true);
      }
    },
  });

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await axiosInstance.get(`/api/get_all_roles`);

        if (response.status === 200 && response.data?.data?.length > 0) {
          setRoles(
            response.data.data.map((role) => ({
              value: role.role_id,
              label: role.role_name,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };
    fetchRoles();
  }, []);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (user_id && roles.length > 0) {
      const fetchUserData = async () => {
        try {
          const response = await axiosInstance.get(
            `/api/get_system_user_by_id/${user_id}`
          );

          if (response.status === 200 && response.data?.data?.length > 0) {
            const userData = response.data.data[0];
            const selectedRole =
              roles.find((r) => r.value === userData.role) || null;
            formik.setValues({
              name: userData.users_name || "",
              email: userData.users_email || "",
              phone_number: userData.contact_number || "",
              role: selectedRole,
            });
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          setSnackbarSeverity("error");
          setSnackbarMessage("Error fetching user data.");
          setSnackbarOpen(true);
        }
      };
      fetchUserData();
    }
  }, [user_id, roles]);

  const handleCancel = () => {
    window.history.back();
  };

  const handleForgotPassword = () => {
    setShowPopup(true);
  };
  const handleClosePopup = () => {
    setShowPopup(false);
    setPassword("");
    setConfirmPassword("");
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handlePasswordReset = () => {
    if (
      !passwordError &&
      !confirmPasswordError &&
      password &&
      confirmPassword
    ) {
      console.log("Password reset successful!");
    } else {
      console.log("Please fix validation errors before submitting.");
    }
  };

  const validatePassword = (value) => {
    const minLength = /.{8,}/; // At least 8 characters
    const upperCase = /[A-Z]/; // At least one uppercase letter
    const number = /[0-9]/; // At least one number
    const specialChar = /[!@#$%^&*]/; // At least one special character

    if (!minLength.test(value)) {
      return "Password must be at least 8 characters.";
    } else if (!upperCase.test(value)) {
      return "Password must contain at least one uppercase letter.";
    } else if (!number.test(value)) {
      return "Password must contain at least one number.";
    } else if (!specialChar.test(value)) {
      return "Password must contain at least one special character.";
    }
    return "";
  };
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordError(validatePassword(newPassword));
  };

  const handleConfirmPasswordChange = (e) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    if (newConfirmPassword !== password) {
      setConfirmPasswordError("Passwords do not match.");
    } else {
      setConfirmPasswordError("");
    }
  };

  return (
    <div
      className={`table-container user-form-page edit-user-form-page ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="table-container-content form-main-body user-form-content">
        <div className="user-form-page-heading edit-user-form-heading">
          <div>
            <h1 className="form-title jost-bold">Edit User</h1>
            <p className="user-form-page-subtitle">
              Update user details and manage the assigned system role.
            </p>
          </div>
          <button
            className="forget-pass-btn edit-user-change-password"
            onClick={handleForgotPassword}
          >
            Change Password
          </button>
        </div>

        <div className="user-form-card edit-user-form-card">
          <div className="form-body-user user-form-body">
            <form className="form-box user-form-box" onSubmit={formik.handleSubmit}>
              <div className="form-body-container user-form-sections">
                <section className="user-form-section user-form-section-last">
                  <div className="user-form-section-header">
                    <div>
                      <h2 className="user-form-section-title">User Details</h2>
                      <p className="user-form-section-description">
                        Review and update contact information and role access for
                        this user.
                      </p>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Name <span className="required">*</span>
                    </label>
                    <div className="form-inputs">
                      <input
                        type="text"
                        className={`input-field ${
                          formik.touched.name && formik.errors.name
                            ? "input-error"
                            : ""
                        }`}
                        name="name"
                        placeholder="Enter User's Name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        required
                      />
                      {formik.touched.name && formik.errors.name && (
                        <div className="error-message">{formik.errors.name}</div>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Email<span className="required">*</span>
                    </label>
                    <div className="form-inputs">
                      <input
                        type="email"
                        className={`input-field ${
                          formik.touched.email && formik.errors.email
                            ? "input-error"
                            : ""
                        }`}
                        name="email"
                        placeholder="Enter Email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        required
                      />
                      {formik.touched.email && formik.errors.email && (
                        <div className="error-message">{formik.errors.email}</div>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Phone Number <span className="required">*</span>
                    </label>
                    <div className="form-inputs">
                      <input
                        type="tel"
                        className={`input-field ${
                          formik.touched.phone_number &&
                          formik.errors.phone_number
                            ? "input-error"
                            : ""
                        }`}
                        name="phone_number"
                        placeholder="Enter Phone Number"
                        value={formik.values.phone_number}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.phone_number &&
                        formik.errors.phone_number && (
                          <div className="error-message">
                            {formik.errors.phone_number}
                          </div>
                        )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Role<span className="required">*</span>
                    </label>
                    <div className="form-inputs">
                      <Select
                        name="role"
                        value={formik.values.role}
                        onChange={(selectedOption) =>
                          formik.setFieldValue("role", selectedOption)
                        }
                        onBlur={() => formik.setFieldTouched("role", true)}
                        options={roles}
                        placeholder="Select User Role"
                        isClearable
                        className={`input-field-role ${
                          formik.touched.role && formik.errors.role
                            ? "input-error"
                            : ""
                        }`}
                      />
                      {formik.touched.role && formik.errors.role && (
                        <div className="error-message">{formik.errors.role}</div>
                      )}
                    </div>
                  </div>
                </section>
              </div>
            </form>
          </div>

          <div className="form-footer">
            <div className="button-group">
              <button
                type="button"
                className="btn btn-cancel"
                onClick={handleCancel}
              >
                CANCEL
              </button>
              <button
                type="submit"
                className="btn btn-save"
                onClick={formik.handleSubmit}
              >
                SAVE
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
      {showPopup && (
        <div className="reset-password-overlay">
          <div className="reset-password-modal">
            <div className="reset-password-header">
              <div>
                <h2 className="reset-password-title">Reset Password</h2>
                <p className="reset-password-subtitle">
                  Update the user password without changing any other account details.
                </p>
              </div>
              <button
                type="button"
                className="reset-password-close"
                onClick={handleClosePopup}
                aria-label="Close reset password popup"
              >
                <FiX />
              </button>
            </div>

            <div className="reset-password-body">
              <div className="reset-password-form">
                <div className="reset-password-field">
                  <label className="reset-password-label">New Password</label>
                  <div className="password-field-shell reset-password-field-shell">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      className="reset-password-input password-field-input"
                      value={password}
                      onChange={handlePasswordChange}
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      className="password-visibility-toggle"
                      onClick={() => setShowNewPassword((prev) => !prev)}
                      aria-label={showNewPassword ? "Hide password" : "Show password"}
                    >
                      {showNewPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                  {passwordError && <p className="error">{passwordError}</p>}
                </div>

                <div className="reset-password-field">
                  <label className="reset-password-label">Confirm Password</label>
                  <div className="password-field-shell reset-password-field-shell">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      className="reset-password-input password-field-input"
                      value={confirmPassword}
                      onChange={handleConfirmPasswordChange}
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      className="password-visibility-toggle"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      aria-label={
                        showConfirmPassword ? "Hide confirm password" : "Show confirm password"
                      }
                    >
                      {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                  {confirmPasswordError && (
                    <p className="error">{confirmPasswordError}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="reset-password-buttons">
              <button className="btn-close" onClick={handleClosePopup}>
                Close
              </button>
              <button
                className="btn-reset"
                onClick={handlePasswordReset}
                disabled={passwordError || confirmPasswordError}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
